import { Box, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import SimpleBox from "../../components/layout/container/SimpleBox";
import dayjs from "dayjs";
import Title from "../../components/typography/Title";
import PartnerGroup from "../../components/layout/container/PartnerGroup";
import ContactForm from "../../components/form/ContactForm";
import Address from "../../components/dataitem/Address";
import PageContent from "../../components/layout/container/PageContent";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { apiPublicPartners } from "../../api/public";

const About = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const location = useLocation();

    const currentPersonId = useSelector(state => state.user.currentPersonId);
    const people = useSelector(state => state.user.people);
    const displayName = useSelector(state => state.user.displayName);
    const userEmail = useSelector(state => state.user.email);
    const person = (people ?? []).find(element => element.id === currentPersonId);

    const [partnerTiers, setPartnerTiers] = React.useState([]);

    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.slice(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    useEffect(() => {
        apiPublicPartners((data, successFlag) => {
            if (successFlag > 0) {
                setPartnerTiers(data.tiers || []);
            }
        });
    }, []);

    const mapHeadquarters = "https://www.google.com/maps/place/ADCB+-+Associa%C3%A7%C3%A3o+Desportiva+e+Cultural+de+Benavente/@38.979072,-8.8070289,19.96z/data=!4m6!3m5!1s0xd191fdb3e0b12f9:0x1136063fd201bb6d!8m2!3d38.9788527!4d-8.8069584!16s%2Fg%2F11cm02fg01?entry=ttu";
    const mapHandball = "https://www.google.com/maps/place/Pavilh%C3%A3o+Gimnodesportivo+da+Escola+Secund%C3%A1ria+de+Benavente/@38.9774964,-8.8093936,17z/data=!3m1!4b1!4m6!3m5!1s0xd191fdb2d361a5b:0x3818e5bf21e09d26!8m2!3d38.9774923!4d-8.8068187!16s%2Fg%2F11c3yqrn28?entry=ttu";

    const text = "Na ADCB, acreditamos na formação de talentos e na inculcação de uma cultura desportiva robusta que acentue os valores de ambição e dedicação. Dedicamo-nos à criação de um ambiente onde cada membro possa crescer, aprender e, sobretudo, sonhar grande. Pois o nosso compromisso vai para além do jogo. É a construção de um caráter que espelha o espírito competitivo, resiliente e unido que define o nosso clube.";

    return (
        <PageContent title="Sobre Nós" message={text}>
            <Container maxWidth="lg" sx={{ marginTop: theme.spacing(10), marginBottom: theme.spacing(10) }}>
                <Box sx={{ boxShadow: theme.shadows[5], borderRadius: theme.spacing(6) }}>
                    <Title label="Apresentação" variant="h2" pt={4} pb={4} pl={10} />
                    <Box sx={{ paddingX: theme.spacing(10), paddingBottom: theme.spacing(4), textAlign: "justify", fontSize: "1.2rem" }}>
                        <p>
                            A <b>ADCB</b> é uma marca de referência no panorama do <b>Andebol Jovem em Portugal</b>.
                        </p>
                        <p>
                            Fundada a 2 de outubro de 1985, conta já com { dayjs().diff(dayjs(new Date(1985, 9, 2)), 'years') } anos de existência,
                            e tem vindo a afirmar-se como um dos emblemas mais representativos em termos da <b>formação</b> de jovens andebolistas no nosso País.
                            Este processo de afirmação tem vindo, consistentemente, a consolidar-se, fruto da grande aposta que é feita na qualidade do seu Corpo
                            Técnico e da <b>dedicação, empenho, entrega e voluntarismo</b> dos seus Dirigentes e Atletas.
                        </p>
                        <p>
                            O estatuto de <b>coletividade de referência</b> em termos do trabalho que é desenvolvido nos seus escalões de Formação
                            tem permitido, não só, a obtenção de <b>resultados desportivos com projeção nacional</b>, como também, o reconhecimento
                            por parte da entidade que superintende o andebol em Portugal (FAP - Federação de Andebol de Portugal), da <b>capacidade
                            organizativa</b> do clube, que se comprova com o reconhecimento do Torneio da Vila de Benavente (que se realiza, anualmente,
                            por ocasião do Carnaval) como Torneio Oficial reconhecido pela FAP, mas também, pelas sucessivas atribuições de eventos de
                            magnitude nacional (Encontro Nacional de Minis Masculinos em 2011 e em 2013 e Fase Final de Sub 16 em 2024) que <b>movimentam
                            largas centenas de atletas e mais de um milhar de pessoas</b> entre participantes, organização e público que assiste a estes eventos.
                        </p>
                        <p>
                            Sob o lema <b>"Formar para Competir"</b> temos vindo a implementar uma política de <b>rigor, disciplina, organização</b>, mas
                            também, de <b>alegria e de gosto por servir a comunidade</b>, que tem permitido projetar o nome de <b>Benavente</b> a nível
                            nacional, extravasando os limites das suas fronteiras concelhias, distritais e regionais. Constituímo-nos como
                            um dos maiores <b>embaixadores da Vila</b> que, orgulhosamente, representamos.
                        </p>
                    </Box>
                </Box>
            </Container>

            {partnerTiers.length > 0 ? (
                <SimpleBox backgroundColor="whiteSmoke" justifyContent="center" flexDirection="column" pt={10} pb={10}>
                    <Title label="Parcerias" variant="h1" textAlign="center" fontWeight="bold" />

                    {partnerTiers.map((tier, index) => (
                        <PartnerGroup
                            key={index}
                            maxWidth="lg"
                            title={partnerTiers.length > 1 ? tier.name : null}
                            partners={tier.partners}
                        />
                    ))}
                </SimpleBox>
            ) : null}

            <SimpleBox justifyContent="center" flexDirection="column" pt={10} pb={10}>
                <span id="contact"></span>
                <Title label="Contacto" textAlign="center" fontWeight="bold" />
                <Container maxWidth="md">
                    <SimpleBox pt={10} pb={10} justifyContent="space-between" flexDirection={"column"} gap={10} fullWidth={true}>
                        <ContactForm fixedName={displayName} fixedEmail={userEmail} fixedPhone={person?.phoneNrMobilePrimary} />
                        <SimpleBox justifyContent="space-around" fullWidth={true} flexWrap="wrap" gap={4}>
                            <Address link={mapHeadquarters} title="Sede" type="headquarters" address="Av. Dr. Manuel Lopes de Almeida, 53" postCode="2130-014 Benavente" />
                            <Address link={mapHandball} title="Pavilhão" type="handball" address="Rua do Trabalhador Rural" postCode="2130-184 Benavente" />
                        </SimpleBox>
                    </SimpleBox>
                </Container>
            </SimpleBox>

        </PageContent>
    );
}

export default About;
